import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MdBook } from "react-icons/md";
import useBreadcrumbs from "../../utils/hooks/useBreadcrumbs";
import CourseService from "../../services/api/CourseService";
import { NotifyContext } from "../../context/notifyContext";
import { Course, CourseAnalytics } from "../../Types/Courses";
import Button from "../../UI/Button/Button";

const initialStats: CourseAnalytics = {
  units_count: 0,
  lessons_count: 0,
  exercise_lessons_count: 0,
  enrolled_students: 0,
  active_students: 0,
  total_completions: 0,
  completion_rate: 0,
  classrooms_using_course: 0,
  assignments_count: 0,
  submissions_count: 0,
};

export default function CourseDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const notify = useContext(NotifyContext);

  const [loading, setLoading] = useState(true);
  const [course, setCourse] = useState<Course | null>(null);
  const [stats, setStats] = useState<CourseAnalytics>(initialStats);

  useBreadcrumbs([
    { label: "Tableau de bord", is_link: true, href: "/app/dashboard" },
    { label: "Cours", icon: <MdBook />, is_link: true, href: "/app/courses" },
    { label: "Détails", is_link: false },
  ]);

  const cards = useMemo(
    () => [
      { label: "Unités", value: stats.units_count },
      { label: "Leçons", value: stats.lessons_count },
      { label: "Leçons avec exercice", value: stats.exercise_lessons_count },
      { label: "Étudiants inscrits", value: stats.enrolled_students },
      { label: "Étudiants actifs", value: stats.active_students },
      { label: "Taux complétion", value: `${stats.completion_rate}%` },
      { label: "Classes liées", value: stats.classrooms_using_course },
      { label: "Devoirs liés", value: stats.assignments_count },
      { label: "Soumissions devoir", value: stats.submissions_count },
    ],
    [stats]
  );

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    CourseService.getCourseAnalytics(Number(id))
      .then((response: any) => {
        setCourse(response?.data?.course || null);
        setStats({ ...initialStats, ...(response?.data?.stats || {}) });
      })
      .catch((error: any) => {
        notify(error?.message || error || "Impossible de charger les détails du cours", "error");
      })
      .finally(() => setLoading(false));
  }, [id]);

  return (
    <div className="space-y-6">
      <div className="bg-white rounded shadow p-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase text-gray-500">Cours</p>
          <h2 className="text-xl font-semibold">{course?.title || "Détails du cours"}</h2>
          <p className="text-sm text-gray-600">{course?.description || "Aucune description."}</p>
        </div>
        <div className="flex gap-2">
          <Button color="info" onClick={() => navigate(`/app/courses/${id}/edit`)}>
            Modifier
          </Button>
          <Button onClick={() => navigate("/app/courses")}>Retour liste</Button>
        </div>
      </div>

      {loading ? <div className="bg-white rounded shadow p-6">Chargement...</div> : null}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {cards.map((card) => (
          <div key={card.label} className="bg-white rounded shadow p-4">
            <p className="text-xs text-gray-500">{card.label}</p>
            <p className="text-xl font-semibold mt-1">{card.value}</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded shadow p-4">
        <h3 className="text-lg font-semibold mb-4">Structure pédagogique</h3>
        {!course?.units?.length ? (
          <p className="text-sm text-gray-500">Aucune unité disponible pour ce cours.</p>
        ) : (
          <div className="space-y-4">
            {course.units.map((unit, unitIndex) => (
              <div key={unit.id} className="border rounded p-3">
                <p className="font-semibold">
                  Unité {unitIndex + 1} - {unit.title}
                </p>
                <div className="mt-2 space-y-2">
                  {(unit.lessons || []).map((lesson, lessonIndex) => (
                    <div
                      key={lesson.id}
                      className="flex items-center justify-between gap-2 text-sm border rounded px-3 py-2"
                    >
                      <div>
                        <span className="font-medium">
                          Leçon {lessonIndex + 1}: {lesson.title}
                        </span>
                        <span className="ml-2 text-gray-500">({lesson.type})</span>
                      </div>
                      {lesson.type === "exercise" && lesson.exercise_id ? (
                        <Button
                          color="info"
                          onClick={() =>
                            navigate(`/app/courses/exercises/${lesson.exercise_id}/details`)
                          }
                        >
                          Voir exercice
                        </Button>
                      ) : (
                        <span className="text-xs text-gray-500">Sans exercice lié</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
